import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  clientApiService,
  AdvanceDetail,
  AdvancePhoto,
} from "../../services/client-api.service";

const ETL_URL =
  import.meta.env.VITE_API_URL_ETL || "https://worker-etl.betalog-ventas.workers.dev";

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  pending: { label: "Pendiente", className: "bg-yellow-100 text-yellow-700" },
  approved: { label: "Aprobado", className: "bg-green-100 text-green-700" },
  rejected: { label: "Rechazado", className: "bg-red-100 text-red-700" },
  draft: { label: "Borrador", className: "bg-slate-100 text-slate-700" },
  under_review: { label: "En revisión", className: "bg-blue-100 text-blue-700" },
};

function formatDate(t: number | null) {
  if (!t) return "—";
  return new Date(t).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatCurrency(n: number) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
  }).format(n);
}

function getPhotoUrl(url: string, photoId: string) {
  // Si url es una key R2 (no empieza con http), usar endpoint público
  if (url.startsWith("http")) return url;
  return `${ETL_URL}/api/public/photos/${photoId}`;
}

export function AdvanceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<AdvanceDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxPhoto, setLightboxPhoto] = useState<AdvancePhoto | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await clientApiService.getAdvance(id);
        setDetail(data);
      } catch (err: any) {
        setError(err?.message || "Error al cargar avance");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [id]);

  // Cerrar lightbox con Escape
  useEffect(() => {
    if (!lightboxPhoto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxPhoto(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightboxPhoto]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
        {error || "Avance no encontrado"}
        <Link to="/dashboard/advances" className="ml-3 underline font-medium">
          ← Volver a avances
        </Link>
      </div>
    );
  }

  const { advance, photos, materials } = detail;
  const status = STATUS_LABELS[advance.status] || {
    label: advance.status,
    className: "bg-slate-100 text-slate-700",
  };

  const totalMaterials = materials.reduce((sum, m) => sum + m.totalCost, 0);

  return (
    <div className="space-y-6">
      {/* Back */}
      <Link
        to="/dashboard/advances"
        className="text-sm text-slate-500 hover:text-primary inline-flex items-center gap-1"
      >
        ← Volver a avances
      </Link>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${status.className}`}>
                {status.label}
              </span>
              {advance.contractorSpecialty && (
                <span className="text-xs font-semibold bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full">
                  🏗️ {advance.contractorSpecialty}
                </span>
              )}
            </div>
            <Link
              to={`/dashboard/projects/${advance.projectId}`}
              className="text-sm text-primary hover:underline font-medium"
            >
              {advance.projectName}
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 mt-1">
              Semana {advance.weekNumber} / {advance.year}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              📅 {formatDate(advance.weekStart)} — {formatDate(advance.weekEnd)}
            </p>
          </div>

          <div className="text-right shrink-0">
            <div className="text-4xl font-bold text-primary">
              +{advance.progressPercentage}%
            </div>
            <div className="text-xs text-slate-400">de avance</div>
          </div>
        </div>

        {advance.description && (
          <p className="text-slate-700 mt-5 whitespace-pre-wrap">{advance.description}</p>
        )}

        {/* Info de aprobación */}
        <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {advance.contractorSpecialty && (
            <div>
              <span className="text-slate-500">Especialidad:</span>
              <span className="ml-2 font-medium text-slate-900">
                {advance.contractorSpecialty}
              </span>
            </div>
          )}
          <div>
            <span className="text-slate-500">Registrado:</span>
            <span className="ml-2 font-medium text-slate-900">
              {formatDate(advance.createdAt)}
            </span>
          </div>
          {advance.status === "approved" && advance.approvedByName && (
            <div>
              <span className="text-slate-500">Aprobado por:</span>
              <span className="ml-2 font-medium text-slate-900">
                {advance.approvedByName}
              </span>
            </div>
          )}
          {advance.status === "approved" && advance.approvedAt && (
            <div>
              <span className="text-slate-500">Fecha aprobación:</span>
              <span className="ml-2 font-medium text-slate-900">
                {formatDate(advance.approvedAt)}
              </span>
            </div>
          )}
        </div>

        {advance.status === "rejected" && advance.rejectedReason && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">
              <strong>Motivo de rechazo:</strong> {advance.rejectedReason}
            </p>
          </div>
        )}
      </div>

      {/* Fotos */}
      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900">
            Evidencia fotográfica
            <span className="ml-2 text-sm font-normal text-slate-500">
              ({photos.length} {photos.length === 1 ? "foto" : "fotos"})
            </span>
          </h2>
        </div>

        {photos.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-3 opacity-40">📷</div>
            <p className="text-slate-500">Sin fotos en este avance</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map((photo) => (
              <button
                key={photo.id}
                onClick={() => setLightboxPhoto(photo)}
                className="group relative aspect-square rounded-xl overflow-hidden bg-slate-100 hover:ring-2 hover:ring-primary transition"
              >
                <img
                  src={getPhotoUrl(photo.url, photo.id)}
                  alt={photo.caption || "Foto de avance"}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5NGEzYjgiIHN0cm9rZS13aWR0aD0iMiI+PHJlY3QgeD0iMyIgeT0iMyIgd2lkdGg9IjE4IiBoZWlnaHQ9IjE4IiByeD0iMiIvPjxjaXJjbGUgY3g9IjguNSIgY3k9IjguNSIgcj0iMS41Ii8+PHBvbHlsaW5lIHBvaW50cz0iMjEgMTUgMTYgMTAgNSAyMSIvPjwvc3ZnPg==";
                  }}
                />
                {photo.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-left">
                    <p className="text-white text-xs line-clamp-1">{photo.caption}</p>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Materiales */}
      {materials.length > 0 && (
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">
            Materiales reportados
            <span className="ml-2 text-sm font-normal text-slate-500">
              ({materials.length})
            </span>
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                  <th className="py-2 pr-3">Material</th>
                  <th className="py-2 px-3 text-right">Cantidad</th>
                  <th className="py-2 px-3">Unidad</th>
                  <th className="py-2 px-3 text-right">Costo unitario</th>
                  <th className="py-2 pl-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {materials.map((m) => (
                  <tr key={m.id} className="border-b border-slate-50">
                    <td className="py-2 pr-3 text-slate-800">{m.name}</td>
                    <td className="py-2 px-3 text-right">{m.quantity}</td>
                    <td className="py-2 px-3 text-slate-500">{m.unit}</td>
                    <td className="py-2 px-3 text-right">{formatCurrency(m.costPerUnit)}</td>
                    <td className="py-2 pl-3 text-right font-medium">
                      {formatCurrency(m.totalCost)}
                    </td>
                  </tr>
                ))}
                <tr className="font-bold">
                  <td colSpan={4} className="py-3 pr-3 text-right text-slate-700">
                    Total:
                  </td>
                  <td className="py-3 pl-3 text-right text-primary">
                    {formatCurrency(totalMaterials)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Lightbox */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxPhoto(null)}
        >
          <button
            onClick={() => setLightboxPhoto(null)}
            className="absolute top-4 right-4 text-white text-3xl hover:opacity-70 z-10"
            aria-label="Cerrar"
          >
            ×
          </button>
          <div className="max-w-5xl max-h-[90vh] relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={getPhotoUrl(lightboxPhoto.url, lightboxPhoto.id)}
              alt={lightboxPhoto.caption || "Foto"}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            {lightboxPhoto.caption && (
              <p className="text-white text-center mt-3">{lightboxPhoto.caption}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}